/*eslint complexity: ["error", 37]*/
import React from 'react';
import _ from 'lodash';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetSynevoDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import momencik from '../../../common/momencik';
import { ExaminedFromExamPreviewLink } from '../Examined/ExaminedFromExamPreviewLink';
import { AttachmentPreviewLink } from '../Attachment/AttachmentPreviewLink';
import { ExamLinkRegister } from '../Exam/ExamLinkRegister';

import { CancelSynevoOrderLink } from './CancelSynevoOrderLink';

interface SynevoSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh?: boolean;
  orderBy?: string;
  withoutCancelButton?: boolean;
}

export const synevoForGrid = (
  synevo: GetSynevoDto,
  setRefresh?: () => void | undefined,
) => {
  return {
    key: synevo.id,
    isInactive: synevo.cancelOrder,
    values: {
      'synevo.systemNumber': synevo.systemNumber,
      'synevo.sentOrderId': synevo.sentOrderId,
      'exam.number': (
        <ExamLinkRegister
          examId={synevo?.exam?.id}
          examNumber={synevo?.exam?.number}
        />
      ),
      'examined.surname': synevo?.exam?.examined && (
        <>
          <ExaminedFromExamPreviewLink examId={synevo?.exam?.id} />{' '}
          {synevo?.exam.examined.surname} {synevo?.exam.examined.firstname}
        </>
      ),
      'company.name': synevo?.exam?.examPackage?.company?.name,
      'examKindDict.name': synevo.synevoOrders
        ?.map(o => o?.supportingExamination?.examKindDict?.name)
        .join(', '),
      'doctor.name': synevo?.doctor?.name,
      'synevo.sentOrder': synevo?.sentOrder ? 'Tak' : 'Nie',
      'synevo.synevoResultId': (() => {
        const orders = synevo?.synevoOrders ?? [];
        const withResult = orders.filter(o => o.synevoResultId).length;
        if (withResult === 0) {
          return 'Nie';
        }
        if (withResult === orders.length) {
          return 'Tak';
        }
        return 'Częściowo';
      })(),
      'synevo.resultDate': _.uniq(
        synevo?.synevoOrders
          ?.filter(o => o.resultDate)
          .map(o => momencik(o.resultDate, 'YYYY-MM-DD')),
      ).join(', '),
      'synevo.creationTs': momencik(synevo.creationTs, 'YYYY-MM-DD HH:mm:ss'),
      attachments: (
        <>
          {_.uniqBy(
            synevo?.synevoOrders?.filter(
              o => o.synevoResultId && o?.attachment,
            ),
            o => o.attachment!.id,
          ).map(o => (
            <AttachmentPreviewLink
              key={o.attachment!.id}
              attachmentId={o.attachment!.id}
              attachmentName={o.attachment!.name || ''}
            />
          ))}
        </>
      ),
      cancel: (
        <>
          {synevo?.sentOrderId &&
            !synevo?.synevoOrders?.some(o => o.synevoResultId) &&
            !synevo.cancelOrder && (
              <CancelSynevoOrderLink
                synevoId={synevo?.id}
                sentOrderId={synevo?.sentOrderId}
                setRefresh={setRefresh}
              />
            )}
        </>
      ),
    },
  };
};

export const SynevoSearch: React.FC<SynevoSearchProps> = props => {
  const columns = [
    { header: 'Numer sys.', property: 'synevo.systemNumber' },
    { header: 'Numer zlecenia', property: 'synevo.sentOrderId' },
    { header: 'Utworzono', property: 'synevo.creationTs' },
    { header: 'Numer badania', property: 'exam.number' },
    { header: 'Badany', property: 'examined.surname' },
    { header: 'Firma', property: 'company.name' },
    { header: 'Rodzaj badania', property: 'examKindDict.name' },
    { header: 'Lekarz', property: 'doctor.name' },
    {
      header: 'Wysłano do Synevo',
      property: 'synevo.sentOrder',
      noFilter: true,
    },
    {
      header: 'Odebrano wynik',
      property: 'synevo.synevoResultId',
      noFilter: true,
      noSorting: true,
    },
    {
      header: 'Data wykonania badania',
      property: 'synevo.resultDate',
      noFilter: true,
      noSorting: true,
    },
    { header: '', property: 'attachments', noSorting: true, noFilter: true },
    ...(props.withoutCancelButton
      ? []
      : [{ header: '', property: 'cancel', noSorting: true, noFilter: true }]),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn={props.orderBy || 'synevo.systemNumber'}
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage="Brak odpowiednich badań do Synevo w systemie"
      refresh={props.refresh}
      withFilterInColumns={true}
    />
  );
};
